import { Icone } from "./Icone";
import { cobre, destinosDe, interceptar } from "./navegacaoMapa";
import type { GrupoDeNavegacao } from "./navegacaoMapa";

import "./NavegacaoFaixa.css";

/**
 * A faixa recolhida — <b>só entra quem tem destino</b>.
 *
 * <p>Item "em construção" fica de fora, nos dois níveis. Um ícone que não leva
 * a lugar nenhum ocupa 44px prometendo tela, e a promessa é pior do que a
 * ausência: na lista esticada o selo "em construção" explica o esmaecido, e
 * aqui não há onde escrever isso.
 *
 * <p>O que se perde ao recolher — os nomes dos setores, o selo do que ainda
 * não existe, a versão do rodapé — volta inteiro ao esticar. É por isso que
 * <b>esticada é o padrão</b>: quem nunca tocar no botão nunca vê a faixa.
 *
 * <p>Saiu de `Navegacao.tsx` pelo teto de 300 do ADR-0013 §2, e a folha dela
 * (`NavegacaoFaixa.css`) é importada DEPOIS de `Navegacao.css` e de
 * `NavegacaoArvore.css` — que é a ordem que as três tinham dentro de `ui.css`.
 * Onde a disputa de estilo é empate, é a ordem que decide.
 */
export function NavegacaoFaixa({
  grupos,
  rotaAtual,
  aoNavegar,
}: {
  grupos: readonly GrupoDeNavegacao[];
  rotaAtual: string;
  aoNavegar(rota: string): void;
}) {
  const destinos = destinosDe(grupos);
  if (destinos.length === 0) return null;

  return (
    <ul className="ui-navegacao__faixa">
      {destinos.map((destino) => {
        const ativo = cobre(destino.rota, rotaAtual);
        return (
          <li key={destino.id} className="ui-navegacao__atalho">
            {/*
              O nome acessível é o `aria-label`, e ele é a razão de o rótulo
              visível ser `aria-hidden`: a dica só aparece no hover e no foco,
              e conteúdo invisível não entra no nome acessível. Sem o
              `aria-label`, quem ouve a tela receberia um link sem nome —
              adivinhação de outro tipo, para quem tem menos de onde adivinhar.
            */}
            <a
              href={destino.rota}
              aria-label={destino.rotulo}
              aria-current={ativo ? "page" : undefined}
              className={`ui-navegacao__icone${ativo ? " ui-navegacao__ativo" : ""}`}
              onClick={interceptar(destino.rota, aoNavegar)}
            >
              <Icone nome={destino.icone} />
            </a>

            {/* Ícone sem rótulo é adivinhação. A dica é o rótulo INTEIRO — não
                uma abreviação, que seria a mesma adivinhação com mais passos —
                e aparece tanto no ponteiro quanto no foco de teclado. */}
            <span className="ui-navegacao__dica" aria-hidden="true">
              {destino.rotulo}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
